import React, { createContext, forwardRef, useContext } from "react";
import { cn } from "./cn";
import useViewport from '../hooks/useViewport';
import Button from "./Button";
import { ChevronRightIcon } from '@heroicons/react/outline';

/**
 * Bun table, "the workhorse". Spec: Component Library "Table".
 * Grid-based: pass a CSS grid template once and every header/row aligns.
 *
 *   <Table columns="32px 1.6fr 1fr 90px">
 *     <Table.Toolbar title="Clients" count={100}>...actions</Table.Toolbar>
 *     <Table.Header>
 *       <span />
 *       <Table.SortButton dir="desc">Household</Table.SortButton>
 *       <span>Net worth</span>
 *       <span />
 *     </Table.Header>
 *     <Table.Row selected>...cells</Table.Row>
 *     <Table.Footer>1-25 of 100 <Table.Pager ... /></Table.Footer>
 *   </Table>
 *
 * Row height ~40px: dense but breathable. Selected = blue wash, hover = mist.
 */
const ColumnsContext = createContext("1fr");

export default function Table({ columns = "1fr", className, children }) {
  return (
    <ColumnsContext.Provider value={columns}>
      <div
        className={cn(
          "border border-bun-line rounded-bun-lg overflow-hidden bg-bun-surface font-instrument text-bun-text",
          className
        )}
      >
        {children}
      </div>
    </ColumnsContext.Provider>
  );
}

function Toolbar({ title, count, className, children }) {
  return (
    <div className={cn("flex items-center gap-[10px] px-4 py-3 border-b border-bun-line-soft", className)}>
      {title && <span className="text-bun-heading font-semibold">{title}</span>}
      {count != null && (
        <span className="text-bun-label normal-case tracking-normal font-semibold bg-bun-mist-deep text-bun-stone rounded-full px-2 py-[2px]">
          {count}
        </span>
      )}
      <div className="flex-1" />
      {children}
    </div>
  );
}

function Header({ className, children }) {
  const columns = useContext(ColumnsContext);
  return (
    <div
      style={{ gridTemplateColumns: columns }}
      className={cn(
        "grid gap-[10px] items-center px-4 py-[9px] border-b border-bun-line",
        "text-bun-label uppercase font-semibold text-bun-faint",
        className
      )}
    >
      {children}
    </div>
  );
}

/** Sortable column header. dir: "asc" | "desc" | undefined (unsorted). size: "sm" (11px) | "md" (12px). */
function SortButton({ dir, onClick, className, children, size = "sm" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1 bg-transparent border-0 p-0 cursor-pointer justify-self-start",
        "uppercase font-semibold tracking-[inherit]",
        size === "md" ? "text-bun-meta" : "text-bun-label",
        dir ? "text-bun-text" : "text-bun-faint hover:text-bun-text",
        className
      )}
    >
      {children}
      {dir && (
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={cn(dir === "asc" && "rotate-180")}
        >
          <path d="M19 14l-7 7-7-7" />
        </svg>
      )}
    </button>
  );
}

function Row({ selected, interactive = true, className, children, ...props }) {
  const columns = useContext(ColumnsContext);
  return (
    <div
      style={{ gridTemplateColumns: columns }}
      className={cn(
        "grid gap-[10px] items-center px-4 py-[11px] text-bun-body font-book",
        "border-b border-bun-line-soft last:border-b-0",
        interactive && "cursor-pointer",
        selected ? "bg-bun-wash" : interactive && "hover:bg-bun-mist",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

/** Optional cell helper for common text treatments. */
function Cell({ muted, faint, strong, right, className, children }) {
  return (
    <span
      className={cn(
        "min-w-0 truncate",
        muted && "text-bun-stone",
        faint && "text-bun-faint",
        strong && "font-strong",
        right && "text-right",
        className
      )}
    >
      {children}
    </span>
  );
}

function Footer({ className, children }) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 px-4 py-[9px] border-t border-bun-line text-bun-meta font-book text-bun-stone",
        className
      )}
    >
      {children}
    </div>
  );
}

/** Prev/next pagination buttons for the footer's right edge. */
function Pager({ onPrev, onNext, prevDisabled, nextDisabled, className }) {
  return (
    <span className={cn("ml-auto inline-flex items-center gap-2", className)}>
      <Button variant="secondary" size="sm" onClick={onPrev} disabled={prevDisabled} aria-label="Previous page">
        &#8249;
      </Button>
      <Button variant="secondary" size="sm" onClick={onNext} disabled={nextDisabled} aria-label="Next page">
        &#8250;
      </Button>
    </span>
  );
}

Table.Toolbar = Toolbar;
Table.Header = Header;
Table.SortButton = SortButton;
Table.Row = Row;
Table.Cell = Cell;
Table.Footer = Footer;
Table.Pager = Pager;

/** Surface for a positioned cell supplied by a virtualized grid. Geometry and
 * saved conditional formatting remain caller data; table styling lives here. */
const WindowCell = forwardRef(function WindowCell({ variant = 'body', unpadded = false, style, children, className, ...props }, ref) {
  return <div ref={ref} data-bun-window-cell {...props}
    style={style} className={cn('bun-hover-actions flex min-w-0 items-center border-b border-r border-bun-line-soft font-instrument text-bun-body text-bun-text',
      variant === 'body' && 'bg-bun-surface',
      variant === 'header' && 'bg-bun-mist text-bun-label font-semibold uppercase tracking-wide text-bun-text',
      variant === 'footer' && 'bg-bun-mist text-bun-meta',
      variant === 'group' && 'bg-bun-mist-deep font-strong',
      !unpadded && 'px-3 py-2', className)}>{children}</div>;
});

// A disabled cell action still displays record data; keep its text and media
// fully visible while preventing edits and hover affordances.
const CellAction = forwardRef(function CellAction({ children, clearAction, wrap = false, align = 'left', className, ...props }, ref) {
  return <div className="flex min-w-0 w-full items-center gap-1">
    <button type="button" ref={ref} {...props} className={cn('min-h-11 min-w-0 flex-1 rounded-bun-sm px-2 py-2 font-instrument text-bun-body text-inherit transition-colors enabled:hover:bg-bun-mist focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bun-ring disabled:cursor-default sm:min-h-[30px]', align === 'right' ? 'text-right' : 'text-left', wrap ? 'whitespace-pre-wrap break-words' : 'truncate', className)}>{children}</button>
    {clearAction}
  </div>;
});

/** Record lists keep table data usable below the desktop grid breakpoint. */
function Responsive({ desktop, children }) {
  const { width } = useViewport();
  return width >= 768
    ? <div className="h-full min-h-0 min-w-0">{desktop}</div>
    : <div className="h-full min-h-0 min-w-0 overflow-y-auto">{children}</div>;
}

Table.WindowCell = WindowCell;
Table.CellAction = CellAction;
Table.Responsive = Responsive;

/** A compact, keyboard-accessible record opener for mobile tables. */
function RecordRow({ children, className, ...props }) {
  return <button type="button" {...props} className={cn(
    'flex min-h-[50px] w-full items-center gap-3 border-b border-bun-line-soft px-4 py-3 text-left font-instrument text-bun-body font-book text-bun-text hover:bg-bun-mist focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-bun-ring',
    className
  )}>
    <span className="min-w-0 flex-1 truncate">{children}</span>
    <ChevronRightIcon className="h-4 w-4 shrink-0 text-bun-faint" aria-hidden="true" />
  </button>;
}

Table.RecordRow = RecordRow;
